import { ObservationPanelNode as ObservationPanelSchema } from '@seen/core/tiptap/schema'
import type { JSONContent } from '@tiptap/core'
import type { Schema } from '@tiptap/pm/model'
import { ReactNodeViewRenderer } from '@tiptap/react'
import {
  ObservationPanelNodeView,
  type ObservationPanelOptions,
} from '#src/components/rich-text-editor/ObservationNode/panel/ObservationPanelNodeView.tsx'
import { resilientNodeView } from '#src/components/rich-text-editor/ResilientNodeView.tsx'

// An instrument captured as observations; its node view is the form (behind
// the structuredCapture flag) and, off, the summary it stores.
export const ObservationPanelNode =
  ObservationPanelSchema.extend<ObservationPanelOptions>({
    addOptions() {
      return {
        participant: undefined,
        recordStatus: undefined,
        isTemplate: undefined,
      }
    },
    addNodeView() {
      return ReactNodeViewRenderer(
        resilientNodeView(ObservationPanelNodeView, { inline: true }),
        {
          // The panel works its own clicks and keys. Left to ProseMirror, a
          // click on a grid cell would move the note's selection and focus,
          // taking focus out of the panel as it is answered.
          stopEvent: ({ event }) => !event.type.startsWith('drag'),
        }
      )
    },
    // Content saved when panels were blocks has them where text cannot go.
    // The starting document is built from it unchecked, so they move into a
    // paragraph of their own first; content set later goes through
    // ProseMirror's replace, which wraps them itself.
    onBeforeCreate() {
      const { content } = this.editor.options
      if (Array.isArray(content)) {
        this.editor.options.content =
          wrapBlockPanels({ type: 'doc', content }, this.editor.schema)
            .content ?? content
      } else if (content && typeof content === 'object') {
        this.editor.options.content = wrapBlockPanels(
          content,
          this.editor.schema
        )
      }
    },
  })

// A copy of `content` with each panel outside a line moved into a paragraph
// of its own, so text can go beside it.
export function wrapBlockPanels(
  content: JSONContent,
  schema: Schema
): JSONContent {
  const inline = (type: string | undefined): boolean =>
    !!type && !!schema.nodes[type]?.inlineContent
  const visit = (node: JSONContent): JSONContent =>
    node.content && !inline(node.type)
      ? {
          ...node,
          content: node.content.map((child) =>
            child.type === ObservationPanelSchema.name
              ? { type: 'paragraph', content: [child] }
              : visit(child)
          ),
        }
      : node
  return visit(content)
}
